import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, useState } from "react";
import Home from "./pages/Home";
import LandingPage from "./pages/LandingPage";
import DashboardV2 from "./pages/DashboardV2";
import DashboardV0 from "./pages/DashboardV0";
import CreateCard from "./pages/CreateCard";
import CardBuilder from "./pages/CardBuilder";
import CardView from "./pages/CardView";
import Bio from "./pages/Bio";
import Web from "./pages/Web";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import CRM from "./pages/CRM";
import AdminScannedCards from "./pages/AdminScannedCards";
import AdminChatSupport from "./pages/AdminChatSupport";
import AdminScannedContacts from "./pages/AdminScannedContacts";
import ReviewCards from "./pages/ReviewCards";
import EditCard from "./pages/EditCard";
import EditBioPage from "./pages/EditBioPage";
import Profile from "./pages/Profile";
import TeamSettings from "./pages/TeamSettings";
import Admin from "./pages/Admin";
import AdminNFC from "./pages/AdminNFC";
import AdminProducts from "./pages/AdminProducts";
import AdminCards from "./pages/AdminCards";
import AdminCardDesigner from "./pages/AdminCardDesigner";
import AdminTemplateBuilder from "./pages/AdminTemplateBuilder";
import UserTemplateEditor from "./pages/UserTemplateEditor";
import NFCClaim from "./pages/NFCClaim";
import HowItWorks from "./pages/HowItWorks";
import Shop from "./pages/Shop";
import ShopCategory from "./pages/ShopCategory";
import ShopSubcategory from "./pages/ShopSubcategory";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Orders from "./pages/Orders";
import TermsAndConditions from "./pages/TermsAndConditions";
import Pricing from "./pages/Pricing";
import QRGeneratorPage from "./pages/QRGeneratorPage";
import CardDesigner from "./pages/CardDesigner";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Features from "./pages/Features";
import ForProfessionals from "./pages/ForProfessionals";
import HowNFCWorks from "./pages/HowNFCWorks";
import Compare from "./pages/Compare";
import Resources from "./pages/Resources";
import AuthHandler from "./pages/AuthHandler";
import GoogleCallback from "./pages/GoogleCallback";
import MyDesigns from "./pages/MyDesigns";
import Visitors from "./pages/Visitors";
import Templates from "./pages/Templates";
import Press from "./pages/Press";
import CardPremium from "./pages/CardPremium";
import FreeBusinessCard from "./pages/FreeBusinessCard";
import {
  FreeQRCodeGenerator,
  EmailSignatureGenerator,
  MeetingThemesGenerator,
  VCardQRGenerator,
  DigitalBusinessCardMaker,
  BusinessCardScanner,
  LinkInBioGenerator
} from "./pages/tools";
import ToolsPage from "./pages/ToolsPage";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { CartProvider } from "./contexts/CartContext";
import { useUserCard } from "./hooks/useUserCard";
import ErrorBoundary from "./components/ErrorBoundary";
import PaymentModal from "./components/PaymentModal";
import TeamInvitationBanner from "./components/TeamInvitationBanner";
import { trackPageView } from "./utils/analytics";
import { initializeClarity, identifyClarityUser, setClarityTag } from "./utils/clarity";

/* Scroll to top on every route change */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname, document.title);
  }, [location]);

  // Initialize Clarity on mount
  useEffect(() => {
    initializeClarity();
  }, []);

  // Identify user in Clarity when authenticated
  useEffect(() => {
    if (user) {
      identifyClarityUser(user.uid);
      setClarityTag('userEmail', user.email);
      setClarityTag('userName', user.displayName || 'Unknown');
    }
  }, [user]);

  return null;
}

// Protected route wrapper for authenticated users
function AuthenticatedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }
  
  return user ? children : <Navigate to="/" replace />;
}

// Protected route for premium users only
function PremiumRoute({ children }) {
  const { user, loading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  
  useEffect(() => {
    const checkSubscription = async () => {
      if (user) {
        try {
          const { subscriptionAPI } = await import('./services/api');
          const status = await subscriptionAPI.getStatus();
          setSubscription(status);
        } catch (e) {
          console.log('Subscription check failed:', e);
        }
      }
      setSubLoading(false);
    };
    checkSubscription();
  }, [user]);
  
  if (loading || subLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }
  
  if (!user) return <Navigate to="/" replace />;
  if (!subscription?.hasAccess) return <Navigate to="/profile" replace />;
  
  return children;
}

// Home route — logged-in users see Dashboard (premium) or Profile (free), guests see Home page
function HomeRoute() {
  const { user, loading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  
  useEffect(() => {
    const checkSubscription = async () => {
      if (user) {
        try {
          const { subscriptionAPI } = await import('./services/api');
          const status = await subscriptionAPI.getStatus();
          setSubscription(status);
        } catch (e) {
          console.log('Subscription check failed:', e);
        }
      }
      setSubLoading(false);
    };
    checkSubscription();
  }, [user]);
  
  if (loading || (user && subLoading)) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
    </div>;
  }
  
  // Logged-in users - always show Dashboard
  if (user) {
    return <DashboardV2 />;
  }
  
  // Guests see new LandingPage
  return <LandingPage />;
}

// Import SubscriptionProvider
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Redirect component for /edit/:username to /card-builder/:username
function EditCardRedirect() {
  const { username } = useParams();
  return <Navigate to={`/card-builder/${username}`} replace />;
}

function App() {
  return (
    <ErrorBoundary componentName="App">
      <HelmetProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <CartProvider>
              <SidebarProvider>
                <div className="App">
                  <BrowserRouter>
                    <ScrollToTop />
                    <AnalyticsTracker />
                <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/dashboard" element={<DashboardV2 />} />
                <Route path="/dashboard-v0" element={<DashboardV0 />} />
                <Route path="/create" element={<Navigate to="/card-builder" replace />} />
                <Route path="/card-builder" element={<CardBuilder />} />
                <Route path="/card-builder/:username" element={<CardBuilder />} />
                <Route path="/card/:username" element={<CardView />} />
                <Route path="/bio/:username" element={<Bio />} />
                <Route path="/web/:username" element={<Web />} />
                <Route path="/bio/:username/edit" element={<EditBioPage />} />
                <Route path="/edit/:username" element={<EditCardRedirect />} />
                <Route path="/my-cards" element={<Navigate to="/profile" replace />} />
                <Route path="/contacts" element={<Contacts />} />
                <Route path="/digital-contacts" element={<Navigate to="/contacts" replace />} />
                <Route path="/contact/:id" element={<ContactDetail />} />
                <Route path="/crm" element={<AuthenticatedRoute><CRM /></AuthenticatedRoute>} />
                <Route path="/admin/scanned-cards" element={<AdminScannedCards />} />
                <Route path="/admin/chat-support" element={<AdminChatSupport />} />
                <Route path="/review-cards" element={<ReviewCards />} />
                <Route path="/profile" element={<AuthenticatedRoute><Profile /></AuthenticatedRoute>} />
                <Route path="/team-settings" element={<AuthenticatedRoute><TeamSettings /></AuthenticatedRoute>} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/nfc" element={<AdminNFC />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/cards" element={<AdminCards />} />
                <Route path="/admin/scanned-contacts" element={<AdminScannedContacts />} />
                <Route path="/admin/card-designer" element={<AdminCardDesigner />} />
                <Route path="/admin/template-builder" element={<AdminTemplateBuilder />} />
                <Route path="/admin/template-builder/:templateId" element={<AdminTemplateBuilder />} />
                <Route path="/c/:code" element={<NFCClaim />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/shop/template/:templateId" element={<UserTemplateEditor />} />
                <Route path="/shop/:categorySlug" element={<ShopCategory />} />
                <Route path="/shop/:categorySlug/:templateSlug" element={<UserTemplateEditor />} />
                <Route path="/cart" element={<AuthenticatedRoute><Cart /></AuthenticatedRoute>} />
                <Route path="/checkout" element={<AuthenticatedRoute><Checkout /></AuthenticatedRoute>} />
                <Route path="/orders" element={<AuthenticatedRoute><Orders /></AuthenticatedRoute>} />
                <Route path="/my-designs" element={<AuthenticatedRoute><MyDesigns /></AuthenticatedRoute>} />
                <Route path="/visitors" element={<AuthenticatedRoute><Visitors /></AuthenticatedRoute>} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/card-premium" element={<CardPremium />} />
                <Route path="/qr-generator" element={<QRGeneratorPage />} />
                <Route path="/design/:templateId" element={<AuthenticatedRoute><CardDesigner /></AuthenticatedRoute>} />
                <Route path="/customize/:templateId" element={<UserTemplateEditor />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/free-business-card" element={<FreeBusinessCard />} />
                <Route path="/free-digital-business-card" element={<FreeBusinessCard />} />
                <Route path="/free-qr-business-card" element={<FreeBusinessCard />} />
                
                {/* Free Tools */}
                <Route path="/tools" element={<ToolsPage />} />
                <Route path="/free-qr-code-generator" element={<FreeQRCodeGenerator />} />
                <Route path="/email-signature-generator" element={<EmailSignatureGenerator />} />
                <Route path="/meeting-themes-generator" element={<MeetingThemesGenerator />} />
                <Route path="/digital-business-card-maker" element={<DigitalBusinessCardMaker />} />
                <Route path="/vcard-qr-code-generator" element={<VCardQRGenerator />} />
                <Route path="/business-card-scanner" element={<BusinessCardScanner />} />
                <Route path="/link-in-bio-generator" element={<LinkInBioGenerator />} />
                
                <Route path="/features" element={<Features />} />
                <Route path="/for-professionals" element={<ForProfessionals />} />
                <Route path="/how-nfc-works" element={<HowNFCWorks />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/press" element={<Press />} />
                <Route path="/faq" element={<Navigate to="/shop" replace />} />
                <Route path="/fav" element={<Navigate to="/shop" replace />} />
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/__/auth/handler" element={<AuthHandler />} />
                <Route path="/google-callback" element={<GoogleCallback />} />
              </Routes>
              <TeamInvitationBanner />
              </BrowserRouter>
              <Toaster />
              <PaymentModal />
            </div>
              </SidebarProvider>
            </CartProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
